import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StayData } from './types';
import SectionTitle from './SectionTitle';
import { StyledPanel } from 'src/components/StyledPanel';
import { StyledFlex } from 'src/components/StyledFlex';
import NumberField from 'src/components/NumberField';
import { countriesISO2 } from 'src/redux-file/helpers';

interface Props {
  data: StayData[];
  onChange: (data: StayData[]) => void;
  onSave: () => Promise<void>;
}

export default function StaysSection(props: Props) {
  const { t } = useTranslation('legacyGhg', {
    keyPrefix: 'employee-survey',
  });

  const { data, onChange, onSave } = props;
  const [changed, setChanged] = useState(false);

  const updateStay = (
    index: number,
    field: keyof StayData,
    value: string | number | null
  ) => {
    const newData = [...data];
    if (field === 'country') {
      newData[index][field] = value as string;
    } else {
      newData[index][field] = value as number;
    }
    onChange(newData);
    setChanged(true);
  };

  const addStay = () => {
    const newData = [
      ...data,
      {
        country: '',
        number_of_nights: 0,
      },
    ];
    onChange(newData);
    setChanged(true);
  };

  const handleDeleteClick = (index: number) => {
    const newData = [...data];
    newData.splice(index, 1);
    onChange(newData);
    setChanged(true);
  };

  return (
    <>
      <SectionTitle>{t('question4')}</SectionTitle>
      <StyledPanel>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('country')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('number-of-nights')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    {t('preview-country-no-data')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((stay, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={stay.country}
                      onChange={(e) =>
                        updateStay(index, 'country', e.target.value)
                      }
                      placeholder={t('country')}
                    >
                      {countriesISO2.map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          {name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <NumberField
                      fullWidth
                      size="small"
                      value={stay.number_of_nights}
                      onChange={(value) =>
                        updateStay(index, 'number_of_nights', value)
                      }
                      placeholder={t('number-of-nights')}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <StyledFlex mt={2} justifyContent="space-between">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addStay}
            size="small"
          >
            {t('add-another')}
          </Button>

          {changed && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onSave().then(() => setChanged(false))}
            >
              {t('save')}
            </Button>
          )}
        </StyledFlex>
      </StyledPanel>
    </>
  );
}
